<template>
	<div>
		<div v-if="showDropdown" class="radio-button-bar radio-button-bar--dropdown">
			<ElPopover
				ref="popover"
				v-model="dropdownActive"
				placement="bottom"
				trigger="manual"
				popper-class="radio-button-bar__popover"
			>
				<ul class="radio-button-bar__picker-list bg-primary no-underline">
					<li
						v-for="item in options"
						:key="item.value"
						class="sz-4"
						:class="{ 'is-checked' : item.value === displayValue }"
						@click="onChange(item.value)"
					>
						<component
							:is="hasLinks && item.value !== displayValue ? 'router-link' : 'div'"
							:to="hasLinks ? item.to : null"
						>
							{{ item.displayValue }}
						</component>
					</li>
				</ul>
				<!-- el-popover relies on deprecated slot syntax to work here -->
				<!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
				<template slot="reference">
					<button
						class="button--no-style radio-button-bar__picker-button"
						@click="togglePopover"
					>
						{{ internalValue }}
						<Inline svg="chevron-down" />
					</button>
				</template>
			</ElPopover>
		</div>
		<ElRadioGroup
			v-else
			v-model="internalValue"
			class="radio-button-bar"
			:class="{ 'radio-button-bar--has-links' : hasLinks, 'app': isApp }"
			@change="onChange"
		>
			<template v-for="item in options">
				<RouterLink
					v-if="isLink(item)"
					:key="item.displayValue"
					class="inline-block"
					:class="{ 'is-checked' : item.value === displayValue }"
					:to="item.to"
				>
					{{ item.displayValue }}
				</RouterLink>
				<ElRadioButton
					v-else
					:key="item.displayValue"
					class="inline-block"
					:label="item.displayValue"
					:disabled="disabled || isLink(item)"
				>
					<Inline v-if="item.icon" :svg="item.icon" />
					{{ item.displayValue }}
				</ElRadioButton>
			</template>
		</ElRadioGroup>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, ref } from 'vue';
	import { Popover, RadioGroup, RadioButton } from '@cognitoforms/element-ui';
	import { Breakpoint } from '../../utilities/breakpoint';
	import clickOutside from '../../utilities/clickOutside';

	export interface RadioButtonBarOptions {
		displayValue: string,
		value: string | object,
		to?: string,
	}

	export default defineComponent({
		name: 'CRadioButtonBar',
		directives: { clickOutside },
		components: {
			ElPopover: Popover,
			ElRadioGroup: RadioGroup,
			ElRadioButton: RadioButton
		},
		props: {
			displayValue: {
				type: String,
				default: undefined
			},
			options: {
				type: Array as PropType<RadioButtonBarOptions[]>,
				default: null
			},
			disabled: {
				type: Boolean,
				default: false
			},
			isApp: {
				type: Boolean,
				default: true
			},
			isDropdown: {
				type: Boolean,
				default: false
			},
			responsiveBreakpoint: {
				type: String,
				default: null,
				validator: displayValue => Object.keys(Breakpoint).some(i => i === displayValue)
			}
		},
		emits: ['change'],
		setup(props) {
			return {
				internalValue: ref(props.displayValue),
				dropdownActive: ref(false),
				dropdownShouldShow: ref(false)
			};
		},
		computed: {
			hasLinks() {
				return this.options.some(item => item.hasOwnProperty('to'));
			},
			selectionIndex() {
				for (let i = 0; i < this.options.length; i++) {
					if (this.options[i].value === this.internalValue)
						return i;
				}
				return null;
			},
			showDropdown() {
				return this.isDropdown || this.dropdownShouldShow;
			}
		},
		watch: {
			displayValue: {
				handler: function (val) {
					const oldDisplayValue = this.internalValue;

					// Handle null/undefined values
					if (val == null) {
						this.internalValue = null;
						return;
					}

					const foundOption = this.options.find(item => item.displayValue === val || item.value === val);
					const newDisplayValue = foundOption ? foundOption.displayValue : val;
					this.internalValue = newDisplayValue;

					if (oldDisplayValue !== newDisplayValue)
						this.onChange(this.internalValue);
				},
				immediate: true
			}
		},
		mounted() {
			if (this.responsiveBreakpoint) {
				this.handleResize();
				window.addEventListener('resize', this.handleResize);
			}
		},
		destroyed() {
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			handleResize() {
				this.dropdownShouldShow = !window.matchMedia(`(min-width: ${Breakpoint[this.responsiveBreakpoint]})`).matches;
			},
			onChange(val) {
				// Necessary because element radio buttons don't decouple display value and internal value
				const selectedItem = this.options.find(item => item.displayValue === val || item.value === val);
				this.$emit('change', selectedItem.value === undefined ? selectedItem.displayValue : selectedItem.value);
				this.dropdownActive = false;
			},
			isLink(item) {
				return !!item.to;
			},
			togglePopover() {
				this.dropdownActive = !this.dropdownActive;
			}
		}
	});
</script>

<style lang="scss">
[data-whatinput='keyboard'] .radio-button-bar:not(.radio-button-bar--has-links):focus-within {
	box-shadow: 0 0 0 2px var(--outline-focused);
}

.radio-button-bar,
.radio-button-bar__picker-list {
	background: var(--bg-primary);
	border: 1px solid var(--border-input);
	border-radius: 30px;
	padding: 4px;

	:root &__picker-button {
		display: flex;
		align-items: center;
		justify-content: center;
		font-family: inherit;
		gap: 8px;
	}

	:root &__picker-list {
		border-radius: 25px;
		box-shadow: var(--shadow-dropdown);
		padding: 4px;
		transform: translateY(10px);

		& .is-checked {
			background: var(--bg-selected);
		}

		li {
			padding: 0;
		}

		a,
		div {
			display: block;
			padding: .75em;
		}

		div {
			cursor: default;
		}
	}

	.el-radio-button__inner,
	a,
	&__picker-button {
		display: inline-block;
	}

	.el-radio-button__inner,
	a,
	&__picker-list li,
	&__picker-button {
		position: relative;
		border-radius: 100px;
		margin: 0;
		padding: 10px 20px;
		cursor: pointer;
		vertical-align: middle;
		text-align: center;
		text-decoration: none;
		white-space: nowrap;
		transition: all .3s cubic-bezier(.645, .045, .355, 1);

		@include b-down($b3) {
			padding: 10px 14px;
		}
	}

	.el-radio-button__orig-radio:checked + .el-radio-button__inner,
	.is-checked,
	&__picker-button {
		background-color: var(--bg-selected-alt);
		border-color: var(--border-selected-alt);
		color: var(--text-selected-alt);
	}

	.el-radio-button__orig-radio:not(:checked) + .el-radio-button__inner {
		@include hover;
	}

	.el-radio-button {
		display: inline-block;
		position: relative;

		&:focus {
			box-shadow: none;
		}

		&__orig-radio {
			position: absolute;
			z-index: $z-1;
			opacity: 0;

			&:disabled {

				& + .el-radio-button__inner {
					opacity: .5;
					pointer-events: none;
				}
			}
		}
	}

	html:not(.website) & {
		display: inline-flex;
		padding: $space__1;
		gap: $space__1;

		label:hover(.is-disabled) span {
			background: var(--bg-hovered);
		}

		.el-radio-button__inner {
			padding: $space__2 $space__3;

			.i-simple {
				vertical-align: bottom;
			}
		}

		&.radio-button-bar--dropdown {

			.radio-button-bar {

				&__picker-button {
					padding: $space__2 $space__3;
				}
			}
		}

		&__popover {

			.radio-button-bar {

				&__picker-list {
					flex-direction: column;
					border-radius: 15px;

					li {
						font-size: $size__2;
						text-align: left;
					}

					div {
						padding: $space__2 $space__3;
					}
				}
			}
		}
	}
}
</style>